import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';
import { authApi } from '../api/api';
const SESSION_KEY = 'pos_session';

function loadSession() {
  try {
    const raw = localStorage.getItem(SESSION_KEY);
    if (!raw) return null;
    const s = JSON.parse(raw);
    if (s && s.loggedIn) return s;
  } catch {
    /* ignore */
  }
  return null;
}

function saveSession(s) {
  if (s) localStorage.setItem(SESSION_KEY, JSON.stringify(s));
  else localStorage.removeItem(SESSION_KEY);
}

const AuthContext = createContext(null);

/**
 * Local “sign in” only stores a session for the UI. It does not validate
 * against User Management — that list is maintained separately by an admin.
 */
export function AuthProvider({ children }) {
  const [session, setSession] = useState(() => loadSession());

  const currentUser = useMemo(() => {
    if (!session?.loggedIn) return null;
    return {
      id: session.userId ?? 'session',
      name: session.name ?? 'User',
      phone: session.phone ?? '',
      email: session.email ?? '',
      role: session.role ?? 'admin',
    };
  }, [session]);

  const logout = useCallback(() => {
    setSession(null);
    saveSession(null);
  }, []);

  const login = useCallback(async (emailRaw, password) => {
    const email = String(emailRaw ?? '').trim();
    if (!email) return { ok: false, error: 'Email is required.' };
    if (!password) return { ok: false, error: 'Password is required.' };

    try {
      const resp = await authApi.login(email, password);
      const data = resp?.data ?? null;
      if (!data || data.status !== 'OK') {
        return {
          ok: false,
          error:
            data?.errorMessage ||
            data?.message ||
            'Invalid email or password.',
        };
      }
      const token = data?.returnData?.access_token;
      if (!token) {
        return { ok: false, error: 'Invalid email or password.' };
      }

      const s = {
        loggedIn: true,
        userId: 'api',
        name: email,
        email,
        phone: '',
        role: 'admin',
        accessToken: token,
        loggedInAt: new Date().toISOString(),
      };
      setSession(s);
      saveSession(s);
      return { ok: true };
    } catch (err) {
      const status = err?.response?.status;
      const data = err?.response?.data;
      if (status) {
        return {
          ok: false,
          error:
            data?.errorMessage ||
            data?.message ||
            `Login failed (HTTP ${status}).`,
        };
      }
      return {
        ok: false,
        error: 'Network error while signing in. Please try again.',
      };
    }
  }, []);

  const value = useMemo(
    () => ({
      currentUser,
      login,
      logout,
    }),
    [currentUser, login, logout]
  );

  return (
    <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
